<template>
    <div class="enhanced-search-container" :class="{ 'focused': isFocused, 'has-value': modelValue }">
        <div class="search-input-wrapper">
            <span class="search-icon">
                <i class="fas fa-search"></i>
            </span>
            <input
                ref="searchInput" 
                type="text" 
                :value="modelValue" 
                :placeholder="placeholder"
                class="search-filter-input" 
                @input="handleInput"
                @focus="handleFocus"
                @blur="handleBlur"
                @keydown.escape="clearSearch"
            />
            <transition name="clear-fade">
                <button 
                    v-if="modelValue && modelValue.length > 0" 
                    class="clear-button" 
                    type="button" 
                    title="Limpiar búsqueda (Esc)"
                    @click="clearSearch"
                >
                    <i class="fas fa-times"></i>
                </button>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, nextTick } from 'vue';

defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    placeholder: {
        type: String,
        default: 'Buscar...'
    }
});

const emit = defineEmits(['update:modelValue', 'clear', 'focus', 'blur']);

const isFocused = ref(false);
const searchInput = ref(null);

const handleInput = (event) => {
    emit('update:modelValue', event.target.value);
};

const handleFocus = () => {
    isFocused.value = true;
    emit('focus');
};

const handleBlur = () => {
    isFocused.value = false;
    emit('blur');
};

const clearSearch = async () => {
    emit('update:modelValue', '');
    emit('clear');
    // Mantener el focus después de limpiar
    await nextTick();
    if (searchInput.value) {
        searchInput.value.focus();
    }
};

// Función para enfocar el input desde el componente padre
const focus = () => {
    if (searchInput.value) {
        searchInput.value.focus();
    }
};

// Exponer la función focus
defineExpose({
    focus
});
</script>
<style scoped>
.enhanced-search-container {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.search-input-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    background: #ffffff;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.enhanced-search-container.focused .search-input-wrapper {
    border-color: #0d6efd;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1), 0 4px 12px rgba(0, 0, 0, 0.1);
}

.enhanced-search-container.has-value .search-input-wrapper {
    border-color: #198754;
}

.search-icon {
    position: absolute;
    left: 16px;
    z-index: 2;
    font-size: 16px;
    color: #6c757d;
    transition: color 0.3s ease;
    pointer-events: none;
}

.enhanced-search-container.focused .search-icon {
    color: #0d6efd;
    transform: scale(1.1);
}

.enhanced-search-container.has-value .search-icon {
    color: #198754;
}

.search-filter-input {
    width: 100%;
    padding: 14px 50px 14px 50px;
    border: none;
    background: transparent;
    font-size: 15px;
    font-weight: 400;
    color: #2c3e50;
    outline: none;
    transition: all 0.3s ease;
    letter-spacing: 0.3px;
}

.search-filter-input::placeholder {
    color: #adb5bd;
    font-style: italic;
    transition: color 0.3s ease;
}

.enhanced-search-container.focused .search-filter-input::placeholder {
    color: #6c757d;
}

.clear-button {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: #dc3545;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 3;
    font-size: 12px;
}

.clear-button:hover {
    background: #c82333;
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.clear-button:active {
    transform: translateY(-50%) scale(0.95);
}

.search-indicator {
    display: flex;
    justify-content: flex-end;
    padding: 0 8px;
    margin-top: 2px;
}

.search-count {
    font-size: 11px;
    color: #6c757d;
    font-weight: 500;
    background: #f8f9fa;
    padding: 2px 8px;
    border-radius: 10px;
    border: 1px solid #e9ecef;
}

.enhanced-search-container.has-value .search-count {
    color: #198754;
    background: #d1e7dd;
    border-color: #badbcc;
}

/* Animaciones */
.clear-fade-enter-active, .clear-fade-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.clear-fade-enter-from, .clear-fade-leave-to {
    opacity: 0;
    transform: translateY(-50%) scale(0.8);
}

.clear-fade-enter-to, .clear-fade-leave-from {
    opacity: 1;
    transform: translateY(-50%) scale(1);
}

/* Responsive */
@media (max-width: 768px) {
    .search-filter-input {
        padding: 12px 45px 12px 45px;
        font-size: 14px;
    }
    
    .search-icon {
        left: 14px;
        font-size: 14px;
    }
    
    .clear-button {
        right: 10px;
        width: 26px;
        height: 26px;
        font-size: 11px;
    }
}

/* Estados especiales */
.enhanced-search-container:hover .search-input-wrapper {
    border-color: #adb5bd;
}

.enhanced-search-container.focused:hover .search-input-wrapper {
    border-color: #0d6efd;
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
    .enhanced-search-container *,
    .enhanced-search-container *::before,
    .enhanced-search-container *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
    }
}
</style>